import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"; // Remove CardDescription import
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { format, subDays, subMonths } from 'date-fns';
import { getFirestore, collection, query, where, getDocs, Timestamp, addDoc } from 'firebase/firestore';
import { LoadingSpinner } from "../ui/loading-spinner";
import { useToast } from "../../hooks/use-toast";
import { dailyLogService } from "../../services/dailyLogService";
import { AlertCircle, Trash2, RefreshCw, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { getAuth } from 'firebase/auth';
import { Progress } from "../ui/progress";

const ArchiveManager = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalArchived: 0,
    olderThan30Days: 0,
    olderThan60Days: 0,
    olderThan90Days: 0,
    olderThan180Days: 0, // Add state for 180 days
  });
  const [selectedPeriod, setSelectedPeriod] = useState('90');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [cleanupProgress, setCleanupProgress] = useState(0);
  const [cleanupStatus, setCleanupStatus] = useState('');
  const [cleanupResults, setCleanupResults] = useState(null);
  
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [creatingTestData, setCreatingTestData] = useState(false);
  const [testResults, setTestResults] = useState(null);
  
  const auth = getAuth();
  const user = auth.currentUser;
  const db = getFirestore();
  const { toast } = useToast();
  
  // Load statistics when component mounts
  useEffect(() => {
    if (user) {
      loadArchiveStats();
    }
  }, [user]);
  
  // Calculate statistics
  const loadArchiveStats = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Define date thresholds
      const now = new Date();
      const days30 = subDays(now, 30);
      const days60 = subDays(now, 60);
      const days90 = subDays(now, 90);
      const days180 = subDays(now, 180); // Define 180 days threshold
      
      // Query for total archived records
      const totalQuery = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', user.uid)
      );
      
      // Query for records older than 30 days
      const older30Query = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', user.uid),
        where('date', '<', Timestamp.fromDate(days30))
      );
      
      // Query for records older than 60 days
      const older60Query = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', user.uid),
        where('date', '<', Timestamp.fromDate(days60))
      );
      
      // Query for records older than 90 days
      const older90Query = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', user.uid),
        where('date', '<', Timestamp.fromDate(days90))
      );
      
      // Query for records older than 180 days
      const older180Query = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', user.uid),
        where('date', '<', Timestamp.fromDate(days180))
      );
      
      // Execute all queries
      const [totalSnapshot, older30Snapshot, older60Snapshot, older90Snapshot, older180Snapshot] = await Promise.all([ // Add older180Snapshot
        getDocs(totalQuery),
        getDocs(older30Query),
        getDocs(older60Query),
        getDocs(older90Query),
        getDocs(older180Query) // Execute 180 day query
      ]);
      
      // Update state with counts
      setStats({
        totalArchived: totalSnapshot.size,
        olderThan30Days: older30Snapshot.size,
        olderThan60Days: older60Snapshot.size,
        olderThan90Days: older90Snapshot.size,
        olderThan180Days: older180Snapshot.size // Update state with 180 day count
      });
      
    } catch (error) {
      console.error('Error loading archive statistics:', error);
      toast({
        title: "Error",
        description: "Failed to load archive statistics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle cleanup confirmation
  const confirmCleanup = () => {
    setShowConfirmDialog(true);
  };
  
  // Execute the cleanup process
  const executeCleanup = async () => {
    setShowConfirmDialog(false);
    setIsDeleting(true);
    setCleanupProgress(0);
    setCleanupStatus('Starting cleanup...');
    
    try {
      const now = new Date();
      let cutoffDate;
      
      // Determine cutoff date based on selection
      if (selectedPeriod === '1') {
        cutoffDate = subDays(now, 1);
      } else if (selectedPeriod === '30') {
        cutoffDate = subDays(now, 30);
      } else if (selectedPeriod === '60') {
        cutoffDate = subDays(now, 60);
      } else if (selectedPeriod === '90') {
        cutoffDate = subDays(now, 90);
      } else if (selectedPeriod === '180') {
        cutoffDate = subDays(now, 180);
      } else {
        throw new Error('Invalid cleanup period selected');
      }
      
      setCleanupStatus(`Deleting records older than ${format(cutoffDate, 'dd MMM yyyy')}...`);
      
      // Start with 10% progress indication
      setCleanupProgress(10);
      
      // Use progressive updates for better UX
      const progressInterval = setInterval(() => {
        setCleanupProgress(prev => {
          // Don't go beyond 90% until we're actually done
          return Math.min(90, prev + 5);
        });
      }, 800);
      
      // Perform the actual cleanup
      const deletedCount = await dailyLogService.cleanupOldArchivedLogs(user.uid, cutoffDate, 50);
      
      // Clear the interval and set to 100%
      clearInterval(progressInterval);
      setCleanupProgress(100);
      
      setCleanupStatus('Cleanup completed successfully!');
      setCleanupResults({
        deletedCount,
        cutoffDate: format(cutoffDate, 'dd MMM yyyy')
      });
      
      // Reload stats after a short delay
      setTimeout(() => {
        loadArchiveStats();
      }, 1500);
      
      toast({
        title: "Cleanup Successful",
        description: `Deleted ${deletedCount} archived records older than ${format(cutoffDate, 'dd MMM yyyy')}`,
        variant: "success",
      });
    } catch (error) {
      console.error('Error cleaning up archived records:', error);
      setCleanupStatus('Cleanup failed. Please try again.');
      
      toast({
        title: "Cleanup Failed",
        description: error.message || "An error occurred during cleanup",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Add a function to create test archive data
  const createTestArchiveData = async () => {
    if (!user) return;
    
    setCreatingTestData(true);
    setTestResults(null);
    
    try {
      // First get some active logs to clone as archived
      const activeLogs = await dailyLogService.getLogEntries(user.uid, { 
        pageSize: 5, 
        invoiced: false 
      });
      
      if (!activeLogs.logs.length) {
        throw new Error('No active logs available to clone. Please create some daily records first.');
      }
      
      // Create backdated archive versions
      const now = new Date();
      const archivedCounts = {
        total: 0,
        thirtyDaysOld: 0,
        sixtyDaysOld: 0,
        ninetyDaysOld: 0
      };
      
      const db = getFirestore();
      
      // Clone each active log with different backdated timestamps
      for (const log of activeLogs.logs.slice(0, 3)) {
        // Create base archive object
        const baseArchiveLog = {
          ...log,
          originalId: log.id,
          invoiced: true,
          invoiceId: 'TEST-INVOICE-' + Math.random().toString(36).substring(2, 7),
          archivedAt: Timestamp.fromDate(now)
        };
        
        // Create 30+ days old archive
        const date30DaysAgo = new Date(now);
        date30DaysAgo.setDate(date30DaysAgo.getDate() - 31);
        await addDoc(collection(db, 'dailyLogsArchive'), {
          ...baseArchiveLog,
          date: Timestamp.fromDate(date30DaysAgo)
        });
        archivedCounts.thirtyDaysOld++;
        
        // Create 60+ days old archive
        const date60DaysAgo = new Date(now);
        date60DaysAgo.setDate(date60DaysAgo.getDate() - 61);
        await addDoc(collection(db, 'dailyLogsArchive'), {
          ...baseArchiveLog,
          date: Timestamp.fromDate(date60DaysAgo)
        });
        archivedCounts.sixtyDaysOld++;
        
        // Create 90+ days old archive
        const date90DaysAgo = new Date(now);
        date90DaysAgo.setDate(date90DaysAgo.getDate() - 91);
        await addDoc(collection(db, 'dailyLogsArchive'), {
          ...baseArchiveLog,
          date: Timestamp.fromDate(date90DaysAgo)
        });
        archivedCounts.ninetyDaysOld++;
        
        archivedCounts.total += 3;
      }
      
      // Update stats after creating test data
      loadArchiveStats();
      
      setTestResults({
        success: true,
        message: `Created ${archivedCounts.total} test archive records:`,
        details: `• ${archivedCounts.thirtyDaysOld} records from 31 days ago\n• ${archivedCounts.sixtyDaysOld} records from 61 days ago\n• ${archivedCounts.ninetyDaysOld} records from 91 days ago`
      });
      
    } catch (error) {
      console.error('Error creating test archive data:', error);
      setTestResults({
        success: false,
        message: 'Failed to create test archive data:',
        details: error.message
      });
      
      toast({
        title: "Error",
        description: "Failed to create test data: " + error.message,
        variant: "destructive",
      });
    } finally {
      setCreatingTestData(false);
    }
  };
  
  // Add a function to create test archive data just over a minute old
  const createRecentTestData = async () => {
    if (!user) return;
    
    setCreatingTestData(true);
    setTestResults(null);
    
    try {
      // First get some active logs to clone as archived
      const activeLogs = await dailyLogService.getLogEntries(user.uid, { 
        pageSize: 2, 
        invoiced: false 
      });
      
      if (!activeLogs.logs.length) {
        throw new Error('No active logs available to clone. Please create some daily records first.');
      }
      
      // Create a record just over a minute old
      const now = new Date();
      const justOverAMinuteAgo = new Date(now.getTime() - 61 * 1000); // 61 seconds ago
      
      const db = getFirestore();
      const archivedCount = { total: 0 };
      
      // Clone the first active log with timestamp just over a minute ago
      const log = activeLogs.logs[0];
      
      // Create base archive object
      const baseArchiveLog = {
        ...log,
        originalId: log.id,
        invoiced: true,
        invoiceId: 'TEST-INVOICE-' + Math.random().toString(36).substring(2, 7),
        archivedAt: Timestamp.fromDate(justOverAMinuteAgo)
      };
      
      // Create an archive document that's just over a minute old
      await addDoc(collection(db, 'dailyLogsArchive'), {
        ...baseArchiveLog,
        date: Timestamp.fromDate(justOverAMinuteAgo)
      });
      archivedCount.total++;
      
      // Update stats after creating test data
      loadArchiveStats();
      
      setTestResults({
        success: true,
        message: `Created ${archivedCount.total} test archive record:`,
        details: `• 1 record from just over a minute ago (${justOverAMinuteAgo.toLocaleTimeString()})\n• This record will appear in archive searches without requiring a long wait`
      });
      
      toast({
        title: "Test Data Created",
        description: "Created a record from just over a minute ago. Refresh stats to see it.",
        variant: "success",
      });
      
    } catch (error) {
      console.error('Error creating recent test archive data:', error);
      setTestResults({
        success: false,
        message: 'Failed to create recent test archive data:',
        details: error.message
      });
      
      toast({
        title: "Error",
        description: "Failed to create test data: " + error.message,
        variant: "destructive",
      });
    } finally {
      setCreatingTestData(false);
    }
  };
  
  return (
    <div className="container mx-auto p-4">
      <Card className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
        <CardHeader>
          <CardTitle className="text-white">Daily Log Archive</CardTitle> {/* Changed title */}
          {/* Replace CardDescription with a regular paragraph */}
          <p className="text-gray-300 text-sm">
            View and manage your archived daily log records {/* Updated description */}
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-[#1F1F2E] p-4 rounded-lg">
              <div className="text-gray-400 text-sm">Total Archived Records</div>
              <div className="text-white text-2xl font-bold">{loading ? <LoadingSpinner size="small" /> : stats.totalArchived}</div>
            </div>
            
            <div className="bg-[#1F1F2E] p-4 rounded-lg">
              <div className="text-gray-400 text-sm">Older than 30 days</div>
              <div className="text-white text-2xl font-bold">{loading ? <LoadingSpinner size="small" /> : stats.olderThan30Days}</div>
            </div>
            
            <div className="bg-[#1F1F2E] p-4 rounded-lg">
              <div className="text-gray-400 text-sm">Older than 60 days</div>
              <div className="text-white text-2xl font-bold">{loading ? <LoadingSpinner size="small" /> : stats.olderThan60Days}</div>
            </div>
            
            <div className="bg-[#1F1F2E] p-4 rounded-lg">
              <div className="text-gray-400 text-sm">Older than 90 days</div>
              <div className="text-white text-2xl font-bold">{loading ? <LoadingSpinner size="small" /> : stats.olderThan90Days}</div>
            </div>
            
            {/* Add card for 180 days */}
            <div className="bg-[#1F1F2E] p-4 rounded-lg">
              <div className="text-gray-400 text-sm">Older than 180 days</div>
              <div className="text-white text-2xl font-bold">{loading ? <LoadingSpinner size="small" /> : stats.olderThan180Days}</div>
            </div>
          </div>
          
          {/* Cleanup Controls */}
          <div className="bg-[#1F1F2E] p-4 rounded-lg">
            <h3 className="text-white text-lg font-medium mb-4">Archive Cleanup</h3> {/* Kept this specific section title */}
            
            <div className="flex flex-col md:flex-row gap-4">
              <div>
                <Label htmlFor="cleanupPeriod" className="text-sm text-gray-300 mb-2 block">Delete records older than</Label>
                <Select 
                  value={selectedPeriod} 
                  onValueChange={setSelectedPeriod}
                >
                  <SelectTrigger id="cleanupPeriod" className="bg-[#1F1F2E] border-purple-500/30 text-white w-[200px]">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A2A3E] border-purple-500/30">
                    <SelectItem value="1" className="text-white hover:bg-purple-500/20">1 day</SelectItem>
                    <SelectItem value="30" className="text-white hover:bg-purple-500/20">30 days</SelectItem>
                    <SelectItem value="60" className="text-white hover:bg-purple-500/20">60 days</SelectItem>
                    <SelectItem value="90" className="text-white hover:bg-purple-500/20">90 days</SelectItem>
                    <SelectItem value="180" className="text-white hover:bg-purple-500/20">180 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-grow flex items-end">
                <div className="flex gap-2">
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={loadArchiveStats}
                    disabled={loading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh Stats
                  </Button>
                  
                  <Button
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={confirmCleanup}
                    disabled={loading || isDeleting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Old Records
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Cleanup Progress (show when cleanup is in progress) */}
            {isDeleting && (
              <div className="mt-4 bg-[#2A2A3E] p-4 rounded-lg border border-purple-500/20">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-white">{cleanupStatus}</span>
                  <span className="text-sm text-gray-300">{cleanupProgress}%</span>
                </div>
                <Progress value={cleanupProgress} className="h-2" />
              </div>
            )}
            
            {/* Cleanup Results (show after successful cleanup) */}
            {!isDeleting && cleanupResults && (
              <div className="mt-4 bg-green-500/10 p-4 rounded-lg border border-green-500/20">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                  <div>
                    <div className="text-green-300 font-medium">Cleanup successful</div>
                    <div className="text-gray-300 text-sm">
                      Deleted {cleanupResults.deletedCount} records older than {cleanupResults.cutoffDate}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Archive Policy Information */}
          <div className="bg-[#1F1F2E] p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
              <div className="text-sm text-gray-300">
                <p className="mb-2">
                  <span className="text-white font-medium">Archive Policy:</span> Daily log records are automatically moved to the archive {/* Updated text */}
                  when they are included in an invoice. This helps keep your active records list clean while maintaining historical data.
                </p>
                <p>
                  <span className="text-white font-medium">Retention:</span> It's recommended to keep archived records for at least 
                  60 days in case you need to refer back to them, but you can clean up older archives to save storage space.
                </p>
              </div>
            </div>
          </div>
          
          {/* Developer Testing Panel */}
          <div className="bg-[#1F1F2E] p-4 rounded-lg border border-yellow-500/20">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-yellow-400 text-lg font-medium">Developer Testing</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTestPanel(!showTestPanel)}
                className="border-yellow-500/40 text-yellow-300"
              >
                {showTestPanel ? 'Hide' : 'Show'}
              </Button>
            </div>
            
            {showTestPanel && (
              <div className="mt-2">
                <div className="bg-yellow-500/5 border border-yellow-500/10 rounded p-3 mb-3">
                  <p className="text-yellow-200 text-sm mb-2">
                    This panel allows you to create test archived records with backdated timestamps 
                    to test the cleanup functionality without waiting for real data to age.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={createTestArchiveData}
                      disabled={creatingTestData}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      {creatingTestData ? (
                        <>
                          <LoadingSpinner size="small" className="mr-2" />
                          Creating data...
                        </>
                      ) : (
                        'Create Test Archive Data'
                      )}
                    </Button>
                    
                    <Button
                      onClick={createRecentTestData}
                      disabled={creatingTestData}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {creatingTestData ? (
                        <>
                          <LoadingSpinner size="small" className="mr-2" />
                          Creating...
                        </>
                      ) : (
                        'Create 1-Minute Old Data'
                      )}
                    </Button>
                  </div>
                </div>
                
                {testResults && (
                  <div className={`mt-3 p-3 rounded border ${testResults.success ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
                    <h4 className={testResults.success ? 'text-green-300' : 'text-red-300'}>{testResults.message}</h4>
                    <pre className="mt-1 whitespace-pre-wrap text-sm text-gray-300">
                      {testResults.details}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-[#2A2A3E] border-red-500/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Deletion</DialogTitle>
            <DialogDescription className="text-gray-300">
              Are you sure you want to permanently delete all archived records older than {selectedPeriod} days? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-[#1F1F2E] p-4 rounded-lg border border-red-500/20 my-4">
            <p className="text-amber-300 text-sm">
              <AlertCircle className="h-4 w-4 inline mr-1" />
              This will delete approximately {
                selectedPeriod === '30' ? stats.olderThan30Days :
                selectedPeriod === '60' ? stats.olderThan60Days :
                selectedPeriod === '90' ? stats.olderThan90Days :
                selectedPeriod === '180' ? stats.olderThan180Days : // Correctly reference olderThan180Days
                selectedPeriod === '1' ? 'some' : 0 // Handle 1 day case if needed, or adjust logic
              } archived records.
            </p>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              className="bg-transparent border-gray-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={executeCleanup}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchiveManager;
